/**
 * Logger con redaccion obligatoria (docs/SECURITY_PRIVACY.md §7).
 * Nunca se registran tokens, claves ni datos personales completos, y nada se
 * envia automaticamente a terceros.
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogDomain = 'sync' | 'ai' | 'db' | 'engine' | 'auth' | 'ui';

export interface LogEntry {
  at: string;
  level: LogLevel;
  domain: LogDomain;
  code: string;
  message: string;
  data?: Record<string, unknown>;
}

const FORBIDDEN_KEYS = [
  'token',
  'access_token',
  'refresh_token',
  'key',
  'apikey',
  'api_key',
  'secret',
  'password',
  'authorization',
  'email',
  'displayname',
];

const RING_SIZE = 500;
const ring: LogEntry[] = [];

/**
 * Sumidero opcional para llevar el registro a algun sitio que sobreviva al
 * proceso. El anillo vive en memoria y muere al cerrar la aplicacion, que es
 * justo cuando mas falta hace: un error de anoche ya no estaba esta mañana.
 *
 * Se declara como un enganche y no como una escritura directa en Dexie para no
 * meter la base de datos dentro de `lib/`: quien lo instala es `data/`, que ya
 * la conoce.
 */
type LogSink = (entry: LogEntry) => void;

let sink: LogSink | null = null;

export function setLogSink(nuevo: LogSink | null): void {
  sink = nuevo;
}

export function redact(value: unknown, depth = 0): unknown {
  if (depth > 4) return '[profundidad-maxima]';
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((item) => redact(item, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = FORBIDDEN_KEYS.includes(key.toLowerCase())
        ? '[redactado]'
        : redact(item, depth + 1);
    }
    return out;
  }
  return value;
}

const LEVEL_ORDER: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };
const minLevel: LogLevel = import.meta.env.DEV ? 'debug' : 'info';

function write(entry: LogEntry): void {
  ring.push(entry);
  if (ring.length > RING_SIZE) ring.shift();

  // Antes del filtro de nivel a proposito: lo que se persiste no depende de
  // que se este imprimiendo por consola. Y protegido, porque un sumidero roto
  // no puede tumbar al que estaba registrando el fallo.
  if (sink !== null) {
    try {
      sink({ ...entry });
    } catch {
      // Sin reintento y sin registrarlo: registrar el fallo del registro es la
      // via mas corta a una recursion infinita.
    }
  }

  if (LEVEL_ORDER[entry.level] < LEVEL_ORDER[minLevel]) return;

  const prefix = `[${entry.domain}] ${entry.code}`;
  const payload = entry.data === undefined ? undefined : redact(entry.data);
  // Unico punto de salida a consola de toda la aplicacion.
  const method = entry.level === 'debug' ? 'log' : entry.level;
  console[method](prefix, entry.message, payload ?? '');
}

function make(level: LogLevel) {
  return (
    domain: LogDomain,
    code: string,
    message: string,
    data?: Record<string, unknown>,
  ): void => {
    const entry: LogEntry = { at: new Date().toISOString(), level, domain, code, message };
    if (data !== undefined) entry.data = data;
    write(entry);
  };
}

export const logger = {
  debug: make('debug'),
  info: make('info'),
  warn: make('warn'),
  error: make('error'),
  /**
   * Copia del anillo local, para `Mas -> Sincronizacion` y para la exportacion
   * del diagnostico. Sale ya redactada: el anillo guarda el dato tal cual, pero
   * en cuanto sale de aqui se enseña o se escribe en un fichero, y ahi no puede
   * viajar nada sensible (docs/SECURITY_PRIVACY.md §7).
   */
  snapshot(): LogEntry[] {
    return ring.map((entry) => {
      const copia: LogEntry = { at: entry.at, level: entry.level, domain: entry.domain, code: entry.code, message: entry.message };
      if (entry.data !== undefined) copia.data = redact(entry.data) as Record<string, unknown>;
      return copia;
    });
  },
  clear(): void {
    ring.length = 0;
  },
};
